import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  ArrowRight,
  Building,
  Eye,
  EyeOff,
  Layers,
  Loader2,
  Network,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { formatDate } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { useRegisterUser, useUserDetails } from '../api';
import {
  useGroups,
  useLanguages,
  useLocations,
  useTtsPlans,
  useAllModules,
  useUserModules,
  useVoicePlans,
} from '../lookups/api';
import { toLegacyExpiry } from '../format';
import { createUserSchema, type CreateUserInput } from '../schema';
import { ModuleSelector, type ModuleSelectorOption } from '../components/ModuleSelector';
import {
  DEDICATED_CLI_MODULE_ID,
  INBOUND_MODULE_ID,
  TTS_MODULE_IDS,
  WEBHOOK_MODULE_ID,
  getModuleMeta,
} from '../components/module-meta';

/* Per-step field arrays — drive validation when the user hits "Next". */
const STEP_FIELDS: Record<number, Array<keyof CreateUserInput>> = {
  0: [
    'username',
    'password',
    'confirmPassword',
    'name',
    'emailid',
    'number',
    'userType',
  ],
  1: ['company', 'address', 'pincode', 'accountType', 'planType', 'planId', 'expiryDate'],
  // languageIds is validated on Step 3 because the picker now lives
  // inline under the TTS module (it only shows when TTS is enabled).
  2: ['moduleIds', 'accessKey', 'secretKey', 'ttsPlanId', 'languageIds'],
  3: ['groupIds', 'locationIds', 'campaignLimit', 'campaignHourLimit'],
  4: [],
};

const STEPS = [
  { id: 'identity', label: 'Identity', description: 'Login & contact', icon: UserRound },
  { id: 'org', label: 'Organization', description: 'Company, plan, validity', icon: Building },
  { id: 'modules', label: 'Modules', description: 'Capabilities', icon: Layers },
  { id: 'allocations', label: 'Allocations', description: 'Groups, locations, languages', icon: Network },
  { id: 'review', label: 'Review', description: 'Confirm details' },
] as const;

export function CreateUserPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const parentId = Number(useAuthStore((s) => s.userId));
  const [showPassword, setShowPassword] = useState(false);

  const isSAdmin = role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;

  const voicePlans = useVoicePlans(parentId);
  const ttsPlans = useTtsPlans(parentId);
  // Module picker semantics:
  //   - SADMIN / SUB_SADMIN: grant any platform module → /api/obd/module
  //   - ADMIN (reseller):    can only grant modules they themselves hold
  //                          → /api/obd/module/list/{adminId}
  // Both hooks have the same return shape; pick at runtime.
  const allModules = useAllModules();
  const parentModules = useUserModules(isSAdmin ? null : parentId);
  const modules = isSAdmin ? allModules : parentModules;
  const groups = useGroups();
  const locations = useLocations();
  const languages = useLanguages(parentId);
  // Parent profile drives the "Submission" checkbox visibility — legacy
  // gates it on `parent.sub === 0` AND `parent.team !== 'VSPL'`.
  const parentProfile = useUserDetails(parentId);
  const showSubmissionFlag =
    (isSAdmin || Number(parentProfile.data?.sub ?? 1) === 0) &&
    parentProfile.data?.team !== 'VSPL';

  const register = useRegisterUser();
  const stepper = useStepper(STEPS.length);
  // Both ADMIN (reseller) and SADMIN/sub-super can pick either type —
  // legacy CreateUser exposes both options to every parent role.
  const userTypeOptions = [
    { value: 'user', label: 'User', hint: 'End customer — runs campaigns directly.' },
    { value: 'admin', label: 'Reseller', hint: 'Sub-admin with their own end users.' },
  ] as const;

  const form = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    mode: 'onTouched',
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      name: '',
      emailid: '',
      number: '',
      company: '',
      address: '',
      pincode: '',
      userType: 'user',
      accountType: '0',
      planType: '0',
      planId: '',
      ttsPlanId: '',
      expiryDate: '',
      // Channels field is no longer exposed in the UI; we hardcode 0 in
      // the submit payload so the legacy endpoint still gets a value.
      channels: '0',
      moduleIds: [],
      accessKey: '',
      secretKey: '',
      ttsCredits: '',
      totalVariable: '',
      groupIds: [],
      locationIds: [],
      languageIds: [],
      campaignLimit: '',
      campaignHourLimit: '',
      isSub: false,
    },
  });

  const selectedModuleIds = form.watch('moduleIds');
  const selectedUserType = form.watch('userType');

  const ttsEnabled = useMemo(
    () => selectedModuleIds.some((id) => TTS_MODULE_IDS.has(id)),
    [selectedModuleIds],
  );

  const moduleOptions = useMemo<ModuleSelectorOption[]>(
    () =>
      (modules.data ?? []).map((m) => {
        const meta = getModuleMeta(m.moduleId);
        return {
          value: String(m.moduleId),
          label: m.moduleName,
          icon: meta.icon,
          ...(meta.description ? { description: meta.description } : {}),
        };
      }),
    [modules.data],
  );

  const groupOptions = useMemo(
    () => (groups.data ?? []).map((g) => ({ value: String(g.groupId), label: g.groupName })),
    [groups.data],
  );
  const locationOptions = useMemo(
    () =>
      (locations.data ?? []).map((l) => ({
        value: String(l.locationId),
        label: l.locationName,
      })),
    [locations.data],
  );
  const languageOptions = useMemo(
    () =>
      (languages.data ?? []).map((l, i) => {
        const code = l.code ?? l.languageCode ?? '';
        const value = code || (l.id != null ? String(l.id) : `lang-${i}`);
        return {
          value,
          label: l.language ?? code ?? `Language ${i + 1}`,
        };
      }),
    [languages.data],
  );

  const handleNext = async () => {
    const fields = STEP_FIELDS[stepper.current];
    if (!fields || fields.length === 0) {
      stepper.next();
      return;
    }
    const ok = await form.trigger(fields);
    if (ok) stepper.next();
  };

  const onSubmit = (input: CreateUserInput) => {
    register.mutate(
      {
        username: input.username.trim(),
        password: input.password,
        name: input.name.trim(),
        emailid: input.emailid.trim(),
        number: input.number.trim(),
        company: input.company.trim(),
        address: input.address?.trim() ?? '',
        pincode: input.pincode?.trim() ?? '',
        parent: parentId,
        accountType: Number(input.accountType) as 0 | 1,
        userType: input.userType,
        planType: input.planType,
        planId: Number(input.planId),
        expiryDate: toLegacyExpiry(input.expiryDate),
        // Channels field is no longer exposed in the UI; hardcode 0.
        channels: 0,
        /*
         * Legacy contract fields (obd_backend UserInfo POJO) that the
         * current UI doesn't surface. Sent with safe defaults so Jackson
         * binding stays predictable and the row is created correctly:
         *   - sub        — "is this a sub-user" flag; we always send 0,
         *                  userType already distinguishes the variant.
         *   - userId2    — secondary user id legacy quirk, unused.
         *   - aclId      — set by sub-super-admin flow only; 0 here.
         *   - settings   — tier-pricing config; created via the Tier
         *                  Pricing page, not the user-create form.
         */
        sub: input.isSub ? 1 : 0,
        userId2: 0,
        aclId: 0,
        settings: {},
        moduleId: input.moduleIds.length > 0 ? input.moduleIds.join(',') : null,
        accessKey:
          input.moduleIds.includes(WEBHOOK_MODULE_ID) ? input.accessKey : undefined,
        secretKey:
          input.moduleIds.includes(WEBHOOK_MODULE_ID) ? input.secretKey : undefined,
        ttsPlanId: ttsEnabled && input.ttsPlanId ? Number(input.ttsPlanId) : undefined,
        // ttsCredits / totalVariable inputs were removed from the UI;
        // send legacy-friendly defaults so the endpoint contract holds.
        ttsCredits: '0',
        totalVariable: undefined,
        /*
         * Legacy wire shape — `groupRows` is `{"groupsList":[…]}` (object),
         * not a bare array. Same for `locationRows` ⇒ `{"locationsList":[…]}`.
         * The backend does `new JSONObject(groupRows)` and throws
         * "A JSONObject text must begin with '{' at character 1" if we ship a
         * bare array string. Wrap the array in the outer object key.
         */
        groupRows:
          input.groupIds.length > 0
            ? JSON.stringify({
                groupsList: input.groupIds.map((id) => {
                  const g = groupOptions.find((o) => o.value === id);
                  return { groupId: Number(id), groupName: g?.label ?? '' };
                }),
              })
            : undefined,
        locationRows:
          input.locationIds.length > 0
            ? JSON.stringify({
                locationsList: input.locationIds.map((id) => {
                  const l = locationOptions.find((o) => o.value === id);
                  return { locationId: Number(id), locationName: l?.label ?? '' };
                }),
              })
            : undefined,
        languages: input.languageIds.length > 0 ? input.languageIds.join(',') : undefined,
        campaignLimit:
          input.userType === 'user' && input.campaignLimit
            ? Number(input.campaignLimit)
            : undefined,
        campaignHourLimit:
          input.userType === 'user' && input.campaignHourLimit
            ? Number(input.campaignHourLimit)
            : undefined,
      },
      {
        onSuccess: (res) => {
          if (res?.message && res.message.toLowerCase().includes('exist')) {
            form.setError('username', { message: res.message });
            stepper.goTo(0);
            return;
          }
          toast.success(res?.message ?? 'User created');
          navigate('/user-list');
        },
        onError: (err) => {
          toast.error(readErrorMessage(err) ?? 'Could not create the user.');
        },
      },
    );
  };

  /* Inline conditional fields rendered inside the module card. */
  const renderModuleExtras = (moduleId: string): React.ReactNode => {
    if (TTS_MODULE_IDS.has(moduleId)) {
      return (
        <>
          <FormField
            control={form.control}
            name="ttsPlanId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  TTS plan<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <Select
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  disabled={ttsPlans.isLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={ttsPlans.isLoading ? 'Loading…' : 'Pick a TTS plan'}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(ttsPlans.data ?? []).map((p) => (
                      <SelectItem key={p.planId} value={String(p.planId)}>
                        {p.planName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="languageIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Languages<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <MultiSelect
                    options={languageOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    placeholder={
                      languages.isLoading ? 'Loading…' : 'Pick at least one language'
                    }
                    disabled={languages.isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      );
    }
    if (moduleId === WEBHOOK_MODULE_ID) {
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="accessKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Access key</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="ak_..." />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="secretKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secret key</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="sk_..." />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      );
    }
    return null;
  };

  const moduleWarning = (id: string): string | null => {
    if (id === INBOUND_MODULE_ID && !selectedModuleIds.includes(DEDICATED_CLI_MODULE_ID)) {
      return 'Inbound campaigns also need the Dedicated CLI module enabled.';
    }
    return null;
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Create User"
        description="Provision a new user account with plan, modules, and resource allocations."
        actions={
          <Button asChild variant="outline">
            <Link to="/user-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <Stepper
            steps={[...STEPS]}
            current={stepper.current}
            completed={stepper.completed}
            onStepClick={(i) => i <= stepper.current && stepper.goTo(i)}
          />
        </CardContent>
      </Card>

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {/* Step 1 — Identity */}
          {stepper.current === 0 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Account</CardTitle>
                  <CardDescription>
                    Login credentials and direct contact for this user.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Username<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} autoComplete="off" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Full name<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} autoComplete="name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Password<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type={showPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                              className="pr-9"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Confirm password<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="emailid"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Email<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} type="email" autoComplete="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Phone<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))
                            }
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            autoComplete="tel"
                            placeholder="10-digit mobile number"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Role</CardTitle>
                  <CardDescription>
                    Pick the user type. Resellers can manage their own end users
                    underneath you.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="userType"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                          >
                            {userTypeOptions.map((opt) => (
                              <label
                                key={opt.value}
                                className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 hover:border-ring"
                              >
                                <RadioGroupItem value={opt.value} className="mt-0.5" />
                                <div>
                                  <div className="text-sm font-medium">{opt.label}</div>
                                  <div className="text-xs text-muted-foreground">{opt.hint}</div>
                                </div>
                              </label>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/*
                    Legacy "Submission" checkbox (`isSub`). Visible only when
                    the parent's own `sub === 0` AND parent's team isn't
                    "VSPL" — gating logic from legacy CreateUser/index.js.
                    When checked, ships `sub: 1` to /register.
                  */}
                  {showSubmissionFlag && (
                    <FormField
                      control={form.control}
                      name="isSub"
                      render={({ field }) => (
                        <FormItem className="mt-4 flex items-start gap-3 rounded-md border bg-card p-3">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(v) => field.onChange(Boolean(v))}
                            />
                          </FormControl>
                          <div className="space-y-0.5">
                            <FormLabel className="cursor-pointer text-sm font-medium">
                              Submission
                            </FormLabel>
                            <p className="text-xs text-muted-foreground">
                              Mark this account as a submission/sub-team user.
                            </p>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Step 2 — Organization, Plan, Validity */}
          {stepper.current === 1 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Organization</CardTitle>
                  <CardDescription>Company and mailing address.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>
                          Company<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>
                          Address<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pincode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Pincode<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} inputMode="numeric" maxLength={10} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Plan &amp; validity</CardTitle>
                  <CardDescription>
                    Billing mode, voice plan, validity window, and channel allocation.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="accountType"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>
                          Account type<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            className="grid grid-cols-2 gap-3"
                          >
                            {(
                              [
                                { value: '0', label: 'Promotional' },
                                { value: '1', label: 'Transactional' },
                              ] as const
                            ).map((opt) => (
                              <label
                                key={opt.value}
                                className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 hover:border-ring"
                              >
                                <RadioGroupItem value={opt.value} />
                                <span className="text-sm font-medium">{opt.label}</span>
                              </label>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="planType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Billing mode<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">Prepaid</SelectItem>
                            <SelectItem value="1">Postpaid</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="planId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Voice plan<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={voicePlans.isLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={voicePlans.isLoading ? 'Loading…' : 'Pick a voice plan'}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(voicePlans.data ?? []).map((p) => (
                              <SelectItem key={p.planId} value={String(p.planId)}>
                                {p.planName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="expiryDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Validity expires on<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            value={field.value ? new Date(field.value) : null}
                            onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                            fromDate={new Date()}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </>
          )}

          {/* Step 3 — Modules */}
          {stepper.current === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Modules &amp; capabilities</CardTitle>
                <CardDescription>
                  Toggle modules on or off. Each module's settings appear inline once enabled, so
                  you can configure TTS plans or webhook keys without leaving this step.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="moduleIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ModuleSelector
                          options={moduleOptions}
                          value={field.value}
                          onChange={field.onChange}
                          renderExtras={renderModuleExtras}
                          getWarning={moduleWarning}
                          disabled={modules.isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {/* Step 4 — Allocations */}
          {stepper.current === 3 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Resources</CardTitle>
                  <CardDescription>
                    Restrict this user to specific groups, locations, and languages they can use.
                    Leave blank for unrestricted access.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="groupIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Groups</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={groupOptions}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Any group"
                            searchPlaceholder="Filter groups…"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="locationIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Locations</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={locationOptions}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Any location"
                            searchPlaceholder="Filter locations…"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="languageIds"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Languages</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={languageOptions}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Any language"
                            searchPlaceholder="Filter languages…"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {selectedUserType === 'user' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Campaign limits</CardTitle>
                    <CardDescription>
                      Optional throttles on how many campaigns this user can run.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="campaignLimit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Total active campaigns</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              inputMode="numeric"
                              min="0"
                              placeholder="No limit"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="campaignHourLimit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Campaigns per hour</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              inputMode="numeric"
                              min="0"
                              placeholder="No limit"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Step 5 — Review */}
          {stepper.current === 4 && (
            <ReviewStep
              form={form}
              voicePlans={voicePlans.data ?? []}
              ttsPlans={ttsPlans.data ?? []}
              moduleOptions={moduleOptions}
              groupOptions={groupOptions}
              locationOptions={locationOptions}
              languageOptions={languageOptions}
              ttsEnabled={ttsEnabled}
            />
          )}

          <div className="sticky bottom-0 -mx-4 mt-2 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.isFirst || register.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-xs text-muted-foreground">
              Step {stepper.current + 1} of {STEPS.length}
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost">
                <Link to="/user-list">Cancel</Link>
              </Button>
              {!stepper.isLast ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button type="submit" disabled={register.isPending}>
                  {register.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create user
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

interface ReviewStepProps {
  form: ReturnType<typeof useForm<CreateUserInput>>;
  voicePlans: Array<{ planId: number; planName: string }>;
  ttsPlans: Array<{ planId: number; planName: string }>;
  moduleOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
  locationOptions: Array<{ value: string; label: string }>;
  languageOptions: Array<{ value: string; label: string }>;
  ttsEnabled: boolean;
}

function ReviewStep({
  form,
  voicePlans,
  ttsPlans,
  moduleOptions,
  groupOptions,
  locationOptions,
  languageOptions,
  ttsEnabled,
}: ReviewStepProps) {
  const v = form.getValues();
  const expiry = v.expiryDate ? formatDate(new Date(v.expiryDate)) : '—';
  const planName = voicePlans.find((p) => String(p.planId) === v.planId)?.planName ?? '—';
  const ttsPlanName = ttsPlans.find((p) => String(p.planId) === v.ttsPlanId)?.planName ?? '—';
  const moduleLabels = v.moduleIds
    .map((id) => moduleOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const groupLabels = v.groupIds
    .map((id) => groupOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const locationLabels = v.locationIds
    .map((id) => locationOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const languageLabels = v.languageIds
    .map((id) => languageOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm before creating. Click any earlier step header to edit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReviewSection title="Identity">
          <Row label="Username" value={v.username} />
          <Row label="Full name" value={v.name} />
          <Row label="Email" value={v.emailid} />
          <Row label="Phone" value={v.number} />
          <Row label="User type" value={v.userType === 'admin' ? 'Reseller' : 'User'} />
        </ReviewSection>
        <ReviewSection title="Organization">
          <Row label="Company" value={v.company} />
          <Row label="Address" value={v.address ?? ''} multiline />
          <Row label="Pincode" value={v.pincode ?? ''} />
        </ReviewSection>
        <ReviewSection title="Plan & validity">
          <Row label="Account type" value={v.accountType === '1' ? 'Transactional' : 'Promotional'} />
          <Row label="Billing mode" value={v.planType === '1' ? 'Postpaid' : 'Prepaid'} />
          <Row label="Voice plan" value={planName} />
          <Row label="Validity expires" value={expiry} />
        </ReviewSection>
        <ReviewSection title="Modules">
          <Row label="Enabled modules" value={moduleLabels || 'None'} multiline />
          {ttsEnabled && (
            <>
              <Row label="TTS plan" value={ttsPlanName} />
              <Row label="Languages" value={languageLabels || '—'} multiline />
            </>
          )}
          {v.moduleIds.includes('3') && (
            <>
              <Row label="Webhook access key" value={v.accessKey ?? '—'} />
              <Row label="Webhook secret key" value={v.secretKey ? '••••••••' : '—'} />
            </>
          )}
        </ReviewSection>
        <ReviewSection title="Allocations">
          <Row label="Groups" value={groupLabels || 'Any'} multiline />
          <Row label="Locations" value={locationLabels || 'Any'} multiline />
          <Row label="Languages" value={languageLabels || 'Any'} multiline />
          {v.userType === 'user' && (
            <>
              <Row label="Campaign limit" value={v.campaignLimit || 'No limit'} />
              <Row label="Campaigns per hour" value={v.campaignHourLimit || 'No limit'} />
            </>
          )}
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{children}</dl>
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'md:col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium text-foreground ${multiline ? 'whitespace-pre-line' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
